import { useState, useEffect, useCallback, useRef } from "react";
import AdminLayout from "../components/AdminLayout";
import {
  Card, Button, Modal, Input, Select, Textarea, EmptyState, SearchBar, Pagination,
  FilterBar, PageHeader, FilterSelect, ListSkeleton, IconButton, Avatar, Badge,
  Field, StickyActions, Divider, AlertBanner, formatINR, cx,
  ListPage, ListSurface, stickyHeadCls, actionColCls, actionHeadCls,
  listTableCls, listCardsCls, thCls, tdCls, actionW,
} from "../components/UI";
import api from "../utils/api";
import toast from "react-hot-toast";
import {
  Plus, Pencil, Trash2, Inbox, Mail, Phone, MapPin, ArrowRight, CircleCheckBig, CalendarDays,
} from "lucide-react";

const LIMIT = 15;
const STATUSES = ["new", "contacted", "qualified", "proposal_sent", "converted", "lost"];

const STATUS_META = {
  new:           { label: "New", cls: "text-brand-700 bg-brand-50 border-brand-200", dot: "bg-brand-500" },
  contacted:     { label: "Contacted", cls: "text-indigo-700 bg-indigo-50 border-indigo-200", dot: "bg-indigo-500" },
  qualified:     { label: "Qualified", cls: "text-violet-700 bg-violet-50 border-violet-200", dot: "bg-violet-500" },
  proposal_sent: { label: "Proposal Sent", cls: "text-amber-700 bg-amber-50 border-amber-200", dot: "bg-amber-500" },
  converted:     { label: "Converted", cls: "text-emerald-700 bg-emerald-50 border-emerald-200", dot: "bg-emerald-500" },
  lost:          { label: "Lost", cls: "text-red-700 bg-red-50 border-red-200", dot: "bg-red-500" },
};

const PRIORITY = {
  low: { label: "Low", variant: "default" },
  medium: { label: "Medium", variant: "warning" },
  high: { label: "High", variant: "danger" },
};

const INIT = { contactName: "", company: "", email: "", phone: "", location: "", subject: "", description: "", workType: "", estimatedValue: "", source: "", priority: "medium", nextFollowUp: "" };
const INIT_CONVERT = { siteName: "", siteCode: "", clientName: "", clientCompany: "", clientEmail: "", clientPhone: "" };

/* Inline status picker — coloured to match the current value */
function StatusPicker({ value, onChange, className = "" }) {
  const m = STATUS_META[value] || STATUS_META.new;
  return (
    <select
      value={value}
      onClick={(e) => e.stopPropagation()}
      onChange={(e) => { e.stopPropagation(); onChange(e.target.value); }}
      className={cx("text-xs font-medium px-2.5 py-1.5 pr-7 rounded-full border cursor-pointer appearance-none transition-colors",
        "bg-[length:12px] bg-[right_0.5rem_center] bg-no-repeat focus:outline-none focus:ring-2 focus:ring-brand-200",
        m.cls, className)}
      style={{ backgroundImage: "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='none' stroke='currentColor' stroke-width='3'%3E%3Cpath d='M6 9l6 6 6-6'/%3E%3C/svg%3E\")" }}
    >
      {STATUSES.map(s => <option key={s} value={s}>{STATUS_META[s].label}</option>)}
    </select>
  );
}

function InquiryCard({ q, onView, onEdit, onDelete, onConvert, onStatus }) {
  return (
    <Card className="p-4" hover>
      {/* actions lead the card, mirroring the table's first column */}
      <div className="flex items-center gap-1.5 pb-3 mb-3 border-b border-ink-100">
        <Button size="xs" variant="outline" icon={<ArrowRight size={13} />} onClick={() => onView(q)}>View</Button>
        {q.status !== "converted" && (
          <Button size="xs" variant="soft" icon={<CircleCheckBig size={13} />} onClick={() => onConvert(q)}
            className="!bg-emerald-50 !text-emerald-700 hover:!bg-emerald-100">Convert</Button>
        )}
        <IconButton size="sm" variant="outline" icon={<Pencil size={15} />} onClick={() => onEdit(q)} title="Edit" className="ml-auto" />
        <IconButton size="sm" variant="danger" icon={<Trash2 size={15} />} onClick={() => onDelete(q._id)} title="Delete" />
      </div>

      <button type="button" onClick={() => onView(q)} className="w-full text-left">
        <div className="flex items-start gap-3">
          <Avatar name={q.contactName} tone="brand" size="md" />
          <div className="min-w-0 flex-1">
            <div className="font-semibold text-ink-900 truncate">{q.contactName}</div>
            {q.company && <div className="text-xs text-ink-400 truncate mt-0.5">{q.company}</div>}
          </div>
          <Badge variant={PRIORITY[q.priority]?.variant || "default"} size="xs" dot>
            {PRIORITY[q.priority]?.label || q.priority}
          </Badge>
        </div>

        <p className="text-sm text-ink-700 mt-3 line-clamp-2">{q.subject}</p>

        <div className="flex flex-wrap items-center gap-x-3 gap-y-1 mt-2 text-xs text-ink-400">
          {q.location && <span className="inline-flex items-center gap-1"><MapPin size={11} />{q.location}</span>}
          {q.workType && <span>{q.workType}</span>}
          {q.estimatedValue > 0 && <span className="text-emerald-600 font-medium tnum">{formatINR(q.estimatedValue, { compact: true })}</span>}
        </div>
      </button>

      <div className="mt-3 pt-3 border-t border-ink-100">
        <StatusPicker value={q.status} onChange={(s) => onStatus(q._id, s)} />
      </div>
    </Card>
  );
}

export default function AdminInquiries() {
  const [inquiries, setInquiries] = useState([]);
  const [total, setTotal] = useState(0);
  const [pages, setPages] = useState(1);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState("");
  const [fStatus, setFStatus] = useState("");
  const [fPrio, setFPrio] = useState("");
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState(null);
  const [selected, setSelected] = useState(null);
  const [form, setForm] = useState(INIT);
  const [convert, setConvert] = useState(INIT_CONVERT);
  const [saving, setSaving] = useState(false);
  const debRef = useRef(null);

  const fetchInquiries = useCallback(async () => {
    setLoading(true);
    try {
      const p = new URLSearchParams({ page, limit: LIMIT });
      if (search) p.set("search", search);
      if (fStatus) p.set("status", fStatus);
      if (fPrio) p.set("priority", fPrio);
      const { data } = await api.get(`/inquiries?${p}`);
      setInquiries(data.data.inquiries || []); setTotal(data.data.total); setPages(data.data.pages);
    } catch { toast.error("Failed to load inquiries"); }
    finally { setLoading(false); }
  }, [page, search, fStatus, fPrio]);

  useEffect(() => { fetchInquiries(); }, [fetchInquiries]);

  const handleSearch = (v) => {
    clearTimeout(debRef.current);
    debRef.current = setTimeout(() => { setSearch(v); setPage(1); }, 300);
    setSearch(v);
  };

  const openCreate = () => { setForm(INIT); setSelected(null); setModal("form"); };
  const openEdit = (q) => {
    setSelected(q);
    setForm({
      contactName: q.contactName || "", company: q.company || "", email: q.email || "", phone: q.phone || "",
      location: q.location || "", subject: q.subject || "", description: q.description || "", workType: q.workType || "",
      estimatedValue: q.estimatedValue || "", source: q.source || "", priority: q.priority || "medium",
      nextFollowUp: q.nextFollowUp ? q.nextFollowUp.split("T")[0] : "",
    });
    setModal("form");
  };
  const openConvert = (q) => {
    setSelected(q);
    setConvert({ ...INIT_CONVERT, clientName: q.contactName || "", clientCompany: q.company || "", clientEmail: q.email || "", clientPhone: q.phone || "" });
    setModal("convert");
  };
  const openView = (q) => { setSelected(q); setModal("view"); };

  const handleSubmit = async (e) => {
    e.preventDefault(); setSaving(true);
    try {
      if (!selected) { await api.post("/inquiries", form); toast.success("Inquiry added!"); }
      else { await api.put(`/inquiries/${selected._id}`, form); toast.success("Updated!"); }
      setModal(null); fetchInquiries();
    } catch (err) { toast.error(err.response?.data?.message || "Failed"); }
    finally { setSaving(false); }
  };

  const handleConvert = async (e) => {
    e.preventDefault(); setSaving(true);
    try {
      await api.post(`/inquiries/${selected._id}/convert`, convert);
      toast.success("Converted to client & site!");
      setModal(null); fetchInquiries();
    } catch (err) { toast.error(err.response?.data?.message || "Failed to convert"); }
    finally { setSaving(false); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this inquiry?")) return;
    try { await api.delete(`/inquiries/${id}`); toast.success("Deleted"); fetchInquiries(); }
    catch { toast.error("Failed"); }
  };

  const updateStatus = async (id, status) => {
    try { await api.put(`/inquiries/${id}`, { status }); toast.success("Status updated"); fetchInquiries(); }
    catch { toast.error("Failed"); }
  };

  const f = k => ({ value: form[k] || "", onChange: e => setForm({ ...form, [k]: e.target.value }) });
  const cv = k => ({ value: convert[k] || "", onChange: e => setConvert({ ...convert, [k]: e.target.value }) });

  const anyFilter = search || fStatus || fPrio;

  return (
    <AdminLayout fullHeight>
      <ListPage>
        <PageHeader
          title="Inquiries"
          subtitle={`${total} lead${total !== 1 ? "s" : ""} in the pipeline`}
          action={<Button onClick={openCreate} icon={<Plus size={16} />} className="w-full sm:w-auto">New Inquiry</Button>}
        />

        {/* ══ Pipeline stage chips — scrollable on mobile ══ */}
        <div className="shrink-0 flex gap-2 overflow-x-auto no-scrollbar pb-0.5 -mx-1 px-1">
          {STATUSES.map(s => {
            const m = STATUS_META[s];
            const active = fStatus === s;
            return (
              <button key={s} onClick={() => { setFStatus(active ? "" : s); setPage(1); }} aria-pressed={active}
                className={cx("flex items-center gap-1.5 text-xs px-3 py-2 rounded-full border font-medium transition-all whitespace-nowrap shrink-0 active:scale-95",
                  m.cls, active ? "ring-2 ring-offset-1 ring-current shadow-xs" : "opacity-70 hover:opacity-100")}>
                <span className={cx("w-1.5 h-1.5 rounded-full", m.dot)} />
                {m.label}
              </button>
            );
          })}
          {fStatus && (
            <button onClick={() => { setFStatus(""); setPage(1); }}
              className="text-xs text-ink-400 hover:text-ink-700 px-2 shrink-0 whitespace-nowrap">Clear</button>
          )}
        </div>

        <FilterBar className="shrink-0">
          <SearchBar value={search} onChange={handleSearch} placeholder="Search name, company, email…" className="flex-1 min-w-[180px]" />
          <FilterSelect value={fPrio} onChange={v => { setFPrio(v); setPage(1); }} ariaLabel="Filter by priority" className="min-w-[140px]">
            <option value="">All Priority</option>
            {["high", "medium", "low"].map(p => <option key={p} value={p}>{PRIORITY[p].label}</option>)}
          </FilterSelect>
        </FilterBar>

        {loading ? (
          <div className="flex-1 min-h-0 overflow-hidden"><ListSkeleton rows={7} cols={7} /></div>
        ) : inquiries.length === 0 ? (
          <Card className="flex-1 min-h-0 grid place-items-center">
            <EmptyState icon={<Inbox size={22} />} title="No inquiries"
              description={anyFilter ? "Try adjusting your search or filters." : "No leads yet — add your first inquiry."}
              action={anyFilter
                ? <Button variant="secondary" onClick={() => { setSearch(""); setFStatus(""); setFPrio(""); setPage(1); }}>Clear filters</Button>
                : <Button onClick={openCreate} icon={<Plus size={16} />}>Add Inquiry</Button>} />
          </Card>
        ) : (
          <ListSurface footer={<Pagination page={page} pages={pages} total={total} limit={LIMIT} onPage={setPage} />}>
            {/* ── Desktop table ── */}
            <table className={listTableCls}>
              <thead className={stickyHeadCls}>
                <tr>
                  <th className={cx(thCls, actionHeadCls, actionW(3))}>Actions</th>
                  <th className={thCls}>Contact</th>
                  <th className={thCls}>Company</th>
                  <th className={thCls}>Location</th>
                  <th className={thCls}>Subject</th>
                  <th className={cx(thCls, "w-[110px]")}>Priority</th>
                  <th className={cx(thCls, "w-[160px]")}>Status</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-ink-100">
                {inquiries.map(q => (
                  <tr key={q._id} className="group hover:bg-ink-50 transition-colors cursor-pointer" onClick={() => openView(q)}>
                    <td className={cx(tdCls, actionColCls, "px-3")} onClick={e => e.stopPropagation()}>
                      <div className="flex gap-0.5">
                        {q.status !== "converted" && (
                          <IconButton size="sm" variant="outline" icon={<CircleCheckBig size={15} />} onClick={() => openConvert(q)}
                            title="Convert to client & site" className="text-emerald-600 hover:bg-emerald-50" />
                        )}
                        <IconButton size="sm" variant="outline" icon={<Pencil size={15} />} onClick={() => openEdit(q)} title="Edit" />
                        <IconButton size="sm" variant="danger" icon={<Trash2 size={15} />} onClick={() => handleDelete(q._id)} title="Delete" />
                      </div>
                    </td>
                    <td className={tdCls}>
                      <div className="flex items-center gap-3 min-w-0">
                        <Avatar name={q.contactName} tone="brand" size="sm" />
                        <div className="min-w-0">
                          <div className="font-semibold text-ink-800 truncate">{q.contactName}</div>
                          <div className="text-xs text-ink-400 truncate">{q.email || q.phone || "—"}</div>
                        </div>
                      </div>
                    </td>
                    <td className={cx(tdCls, "text-xs text-ink-600")}>
                      <div className="truncate">{q.company || <span className="text-ink-300">—</span>}</div>
                    </td>
                    <td className={cx(tdCls, "text-xs text-ink-500")}>
                      <div className="truncate">{q.location || <span className="text-ink-300">—</span>}</div>
                    </td>
                    <td className={tdCls}>
                      <div className="text-ink-700 truncate">{q.subject}</div>
                      {q.workType && <div className="text-xs text-ink-400 truncate">{q.workType}</div>}
                    </td>
                    <td className={tdCls}>
                      <Badge variant={PRIORITY[q.priority]?.variant || "default"} dot>{PRIORITY[q.priority]?.label || q.priority}</Badge>
                    </td>
                    <td className={tdCls}>
                      <StatusPicker value={q.status} onChange={(s) => updateStatus(q._id, s)} className="max-w-full" />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            {/* ── Mobile / tablet cards ── */}
            <div className={listCardsCls}>
              {inquiries.map(q => (
                <InquiryCard key={q._id} q={q} onView={openView} onEdit={openEdit} onDelete={handleDelete}
                  onConvert={openConvert} onStatus={updateStatus} />
              ))}
            </div>
          </ListSurface>
        )}
      </ListPage>

      {/* ══ View ══ */}
      <Modal open={modal === "view"} onClose={() => setModal(null)} title={selected?.contactName || "Inquiry"}
        subtitle={selected?.company} size="lg" icon={<Inbox size={18} />}>
        {selected && (
          <div className="space-y-5">
            <div className="flex items-center gap-2 flex-wrap">
              <span className={cx("text-xs font-semibold px-3 py-1 rounded-full border", (STATUS_META[selected.status] || STATUS_META.new).cls)}>
                {(STATUS_META[selected.status] || STATUS_META.new).label}
              </span>
              <Badge variant={PRIORITY[selected.priority]?.variant || "default"} dot>
                {PRIORITY[selected.priority]?.label} priority
              </Badge>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {selected.email && (
                <a href={`mailto:${selected.email}`} className="flex items-center gap-3 bg-ink-50 hover:bg-ink-100 rounded-xl p-3 transition-colors">
                  <Mail size={16} className="text-ink-400 shrink-0" />
                  <span className="text-sm text-ink-800 truncate">{selected.email}</span>
                </a>
              )}
              {selected.phone && (
                <a href={`tel:${selected.phone}`} className="flex items-center gap-3 bg-ink-50 hover:bg-ink-100 rounded-xl p-3 transition-colors">
                  <Phone size={16} className="text-ink-400 shrink-0" />
                  <span className="text-sm text-ink-800">{selected.phone}</span>
                </a>
              )}
            </div>

            <div className="bg-ink-50 rounded-2xl p-4 grid grid-cols-2 gap-4">
              <Field label="Location" value={selected.location} />
              <Field label="Source" value={selected.source} />
              <Field label="Work Type" value={selected.workType} />
              <Field label="Est. Value" value={selected.estimatedValue ? formatINR(selected.estimatedValue) : null} />
            </div>

            {selected.subject && (
              <div>
                <p className="text-[11px] font-semibold text-ink-400 uppercase tracking-wider mb-1.5">Subject</p>
                <p className="text-sm text-ink-800">{selected.subject}</p>
              </div>
            )}
            {selected.description && (
              <div>
                <p className="text-[11px] font-semibold text-ink-400 uppercase tracking-wider mb-1.5">Description</p>
                <p className="text-sm text-ink-700 whitespace-pre-wrap leading-relaxed">{selected.description}</p>
              </div>
            )}
            {selected.nextFollowUp && (
              <AlertBanner type="warning" title="Follow-up scheduled"
                message={new Date(selected.nextFollowUp).toLocaleDateString("en-IN", { weekday: "long", day: "numeric", month: "long", year: "numeric" })} />
            )}

            <div className="flex flex-col-reverse sm:flex-row gap-2.5 pt-1">
              <Button variant="secondary" onClick={() => { setModal(null); setTimeout(() => openEdit(selected), 120); }} block className="sm:w-auto">Edit</Button>
              {selected.status !== "converted" && (
                <Button variant="success" iconRight={<ArrowRight size={15} />}
                  onClick={() => { setModal(null); setTimeout(() => openConvert(selected), 120); }} block className="sm:w-auto">
                  Convert to Client
                </Button>
              )}
            </div>
          </div>
        )}
      </Modal>

      {/* ══ Create / edit ══ */}
      <Modal open={modal === "form"} onClose={() => setModal(null)} title={selected ? "Edit Inquiry" : "New Inquiry"}
        subtitle={selected ? selected.contactName : "Capture a new lead"} size="lg" icon={<Inbox size={18} />}>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Input label="Contact Name" required {...f("contactName")} placeholder="Full name" />
            <Input label="Company" {...f("company")} placeholder="Organisation name" />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Input label="Email" type="email" {...f("email")} placeholder="contact@company.com" icon={<Mail size={15} />} />
            <Input label="Phone" type="tel" {...f("phone")} placeholder="+91 XXXXX XXXXX" icon={<Phone size={15} />} />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Input label="Location" {...f("location")} placeholder="City / Region" icon={<MapPin size={15} />} />
            <Input label="Work Type" {...f("workType")} placeholder="e.g. Topographic Survey" />
          </div>
          <Input label="Subject" required {...f("subject")} placeholder="Brief description of the requirement" />
          <Textarea label="Description" {...f("description")} placeholder="Detailed requirement…" />
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
            <Select label="Priority" {...f("priority")}>
              {["low", "medium", "high"].map(p => <option key={p} value={p}>{PRIORITY[p].label}</option>)}
            </Select>
            <Input label="Est. Value" type="number" {...f("estimatedValue")} placeholder="0" suffix="₹" />
            <Input label="Source" {...f("source")} placeholder="Website / Referral" wrapClass="col-span-2 sm:col-span-1" />
          </div>
          <Input label="Next Follow-up" type="date" {...f("nextFollowUp")} icon={<CalendarDays size={15} />} />

          <StickyActions>
            <Button type="button" variant="secondary" onClick={() => setModal(null)} block className="sm:w-auto">Cancel</Button>
            <Button type="submit" loading={saving} block className="sm:w-auto">{selected ? "Save Changes" : "Add Inquiry"}</Button>
          </StickyActions>
        </form>
      </Modal>

      {/* ══ Convert ══ */}
      <Modal open={modal === "convert"} onClose={() => setModal(null)} title="Convert to Client & Site"
        subtitle={selected?.contactName} size="lg" icon={<CircleCheckBig size={18} />}>
        <form onSubmit={handleConvert} className="space-y-4">
          <AlertBanner type="info" message="This creates a new Client and a new Site from this inquiry, and marks the lead converted." />

          <Divider label="Client details" />
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Input label="Client Name" required {...cv("clientName")} />
            <Input label="Company" {...cv("clientCompany")} />
            <Input label="Email" type="email" {...cv("clientEmail")} icon={<Mail size={15} />} />
            <Input label="Phone" type="tel" {...cv("clientPhone")} icon={<Phone size={15} />} />
          </div>

          <Divider label="Site details" />
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Input label="Site Name" required {...cv("siteName")} />
            <Input label="Site Code" {...cv("siteCode")} className="font-mono" />
          </div>

          <StickyActions>
            <Button type="button" variant="secondary" onClick={() => setModal(null)} block className="sm:w-auto">Cancel</Button>
            <Button type="submit" variant="success" loading={saving} block className="sm:w-auto">Convert</Button>
          </StickyActions>
        </form>
      </Modal>
    </AdminLayout>
  );
}
